import React, { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';

interface SectorData {
  label: string;
  value: number;
}

interface SectorChartProps {
  data: SectorData[];
  total: number;
}

export const SectorChart: React.FC<SectorChartProps> = ({ data, total }) => {
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => b.value - a.value).slice(0, 10);
  }, [data]);

  const colors = [
    'from-blue-500 to-blue-600',
    'from-indigo-500 to-indigo-600',
    'from-purple-500 to-purple-600',
    'from-pink-500 to-pink-600',
    'from-rose-500 to-rose-600',
    'from-orange-500 to-orange-600',
    'from-amber-500 to-amber-600',
    'from-emerald-500 to-emerald-600',
    'from-teal-500 to-teal-600',
    'from-cyan-500 to-cyan-600',
  ];

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
          <BarChart3 className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Sector Distribution</h3>
          <p className="text-sm text-gray-500">Top 10 sectors by recommendation count</p>
        </div>
      </div>

      <div className="space-y-4">
        {sortedData.map((sector, index) => {
          const percentage = (sector.value / Math.max(1, total)) * 100;
          const colorClass = colors[index % colors.length];
          
          return (
            <div key={index} className="group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center text-white text-xs font-bold shadow-md`}>
                    {index + 1}
                  </span>
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {sector.label}
                  </span>
                </div>
                <div className="flex items-center gap-3 ml-3">
                  <span className="text-xs font-medium text-gray-500 min-w-[45px] text-right">
                    {percentage.toFixed(1)}%
                  </span>
                  <span className="text-sm font-bold text-gray-900 min-w-[35px] text-right">
                    {sector.value}
                  </span>
                </div>
              </div>
              
              <div className="relative w-full bg-gray-100 rounded-full h-3 overflow-hidden shadow-inner">
                <div
                  className={`h-full bg-gradient-to-r ${colorClass} rounded-full transition-all duration-700 ease-out shadow-md relative overflow-hidden`}
                  style={{ width: `${percentage}%` }}
                >
                  {/* Animated shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};
